import { useState, useEffect, useRef, useCallback } from 'react';
import { Html5Qrcode } from 'html5-qrcode';

const hasNativeDetector = () =>
  typeof window !== 'undefined' && 'BarcodeDetector' in window;

export const useBarcodeScanner = (onDetected, active = true) => {
  const scannerRef  = useRef(null);
  const videoRef    = useRef(null);
  const streamRef   = useRef(null);
  const rafRef      = useRef(null);
  const cooldown    = useRef(false);
  const lastCode    = useRef(null);
  const activeRef   = useRef(active); // ref para acceder en listeners sin stale closure

  const [scanning, setScanning] = useState(false);
  const [error,    setError]    = useState(null);

  // Mantener activeRef sincronizado
  useEffect(() => { activeRef.current = active; }, [active]);

  /* ── Detener SOLO la cámara, sin tocar nada más ── */
  const stopCamera = useCallback(async () => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    if (scannerRef.current) {
      try { await scannerRef.current.stop(); } catch (_) {}
      try { scannerRef.current.clear(); }     catch (_) {}
      scannerRef.current = null;
    }
    setScanning(false);
  }, []);

  /* ── Callback con cooldown ── */
  const handleDetected = useCallback((text) => {
    if (cooldown.current) return;
    cooldown.current = true;
    lastCode.current = text;
    onDetected(text);
    setTimeout(() => {
      cooldown.current = false;
      lastCode.current = null;
    }, 2500);
  }, [onDetected]);

  /* ── Modo NATIVO: BarcodeDetector ── */
  const startNative = useCallback(async () => {
    try {
      const supported = await BarcodeDetector.getSupportedFormats(); // eslint-disable-line no-undef
      const formats = supported.includes('ean_13')
        ? ['ean_13','ean_8','code_128','code_39','upc_a','upc_e','qr_code']
        : supported;
      const detector = new BarcodeDetector({ formats }); // eslint-disable-line no-undef

      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment', width: { ideal: 1280 }, height: { ideal: 720 } }
      });
      streamRef.current = stream;

      const container = document.getElementById('barcode-reader');
      if (!container) throw new Error('no container');

      // Limpiar y recrear video siempre para evitar estado congelado
      container.innerHTML = '';
      const video = document.createElement('video');
      video.style.cssText = 'width:100%;height:100%;object-fit:cover;';
      video.setAttribute('playsinline', '');
      video.setAttribute('muted', '');
      video.muted = true;
      container.appendChild(video);
      videoRef.current = video;
      video.srcObject = stream;
      await video.play();

      setScanning(true);
      setError(null);

      let pendingCode  = null;
      let pendingCount = 0;

      const tick = async () => {
        if (!streamRef.current) return;
        try {
          if (video.readyState === video.HAVE_ENOUGH_DATA) {
            const barcodes = await detector.detect(video);
            if (barcodes.length > 0) {
              const code = barcodes[0].rawValue;
              if (code === pendingCode) {
                pendingCount++;
                if (pendingCount >= 2) {
                  pendingCode  = null;
                  pendingCount = 0;
                  handleDetected(code);
                }
              } else {
                pendingCode  = code;
                pendingCount = 1;
              }
            }
          }
        } catch (_) {}
        rafRef.current = requestAnimationFrame(tick);
      };
      rafRef.current = requestAnimationFrame(tick);

    } catch (e) {
      await startFallback(); // eslint-disable-line no-use-before-define
    }
  }, [handleDetected]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── Modo FALLBACK: html5-qrcode ── */
  const startFallback = useCallback(async () => {
    await new Promise(r => setTimeout(r, 100));
    try {
      const el = document.getElementById('barcode-reader');
      if (!el) { setError('Error al iniciar cámara'); return; }

      // Limpiar contenido previo
      el.innerHTML = '';

      const scanner = new Html5Qrcode('barcode-reader');
      scannerRef.current = scanner;
      setError(null);

      let fbPending = null;
      let fbCount   = 0;

      await scanner.start(
        { facingMode: 'environment' },
        { fps: 15, aspectRatio: 1.7, disableFlip: false },
        (text) => {
          if (text === fbPending) {
            fbCount++;
            if (fbCount >= 2) {
              fbPending = null;
              fbCount   = 0;
              handleDetected(text);
            }
          } else {
            fbPending = text;
            fbCount   = 1;
          }
        },
        () => {}
      );
      setScanning(true);
    } catch (e) {
      const msgs = {
        NotAllowedError: 'Permiso de cámara denegado.',
        NotFoundError:   'No se encontró cámara.',
      };
      setError(msgs[e.name] || 'Error de cámara: ' + e.message);
      setScanning(false);
    }
  }, [handleDetected]);

  /* ── Iniciar ── */
  const start = useCallback(async () => {
    await stopCamera();
    // Pequeña pausa para que el DOM se limpie antes de reiniciar
    await new Promise(r => setTimeout(r, 150));
    if (!activeRef.current) return;
    if (hasNativeDetector()) {
      await startNative();
    } else {
      await startFallback();
    }
  }, [stopCamera, startNative, startFallback]);

  /* ── Ciclo de vida según prop active ── */
  useEffect(() => {
    if (active) {
      start();
    } else {
      stopCamera();
    }
    return () => { stopCamera(); };
  }, [active]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── Manejar minimizar / restaurar app ── */
  useEffect(() => {
    const handleVisibility = () => {
      if (!activeRef.current) return;
      if (document.visibilityState === 'hidden') {
        // App minimizada — detener cámara limpiamente
        stopCamera();
      } else {
        // App restaurada — reiniciar desde cero
        start();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [start, stopCamera]);

  return {
    videoRef,
    scanning,
    error,
    devices: [],
    selDev: null,
    switchCamera: () => {},
  };
};
